import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { supabase, toError } from '../supabase';
import { PaperData } from '../../components/papers/types';
import { normalizePaperData } from '../paperAuthors';
import {
    ProjectPaperFeedback,
    ProjectFeedFeedbackFilter,
    ProjectFeedRelevanceFilter,
} from '../projectPaperFeedback';
import { withTimeout } from '../asyncTimeout';

const PAGE_SIZE = 20;
export const FEED_REQUEST_TIMEOUT_MS = 15000;

function normalizeFeedPaper(item: any): FeedPaper {
    return {
        ...item,
        paper: item.paper ? normalizePaperData(item.paper) as PaperData : item.paper,
    };
}

export type ProjectFeedSortOption = 'date' | 'relevance';

export interface FeedPaper {
    id: string;
    short_id: string;
    slug: string;
    paper_title: string | null;
    summary: string | null;
    relevance_score: number | null;
    relevance_class: number | null;
    matching_details: any;
    personalized_note: string | null;
    ready_at: string | null;
    paper: PaperData;
    project_paper_feedback: ProjectPaperFeedback | null;
}

const FEED_SELECT_BASE = `
    id, short_id, slug, paper_title, summary,
    relevance_score, relevance_class, matching_details,
    personalized_note, ready_at,
    paper:paper_id (
        id, short_id, slug, title, abstract, authors, published_date,
        pdf_url, url, doi, source_id, source_paper_id, source_paper_version
    )
`;

function getFeedSelect(feedbackFilter: ProjectFeedFeedbackFilter) {
    const feedbackRelation = feedbackFilter === 'all' || feedbackFilter === 'unrated'
        ? 'project_paper_feedback'
        : 'project_paper_feedback!inner';
    return `
        ${FEED_SELECT_BASE},
        ${feedbackRelation} (
            vote, downvote_reason, updated_at
        )
    `;
}

function applyFeedbackFilter(query: any, feedbackFilter: ProjectFeedFeedbackFilter) {
    if (feedbackFilter === 'unrated') {
        return query.is('project_paper_feedback', null);
    }

    if (feedbackFilter === 'liked') {
        return query.eq('project_paper_feedback.vote', 'upvote');
    }

    if (feedbackFilter === 'disliked') {
        return query.eq('project_paper_feedback.vote', 'downvote');
    }

    if (feedbackFilter === 'starred') {
        return query.eq('project_paper_feedback.vote', 'star');
    }

    if (feedbackFilter === 'not-relevant') {
        return query
            .eq('project_paper_feedback.vote', 'downvote')
            .eq('project_paper_feedback.downvote_reason', 'not_relevant');
    }

    if (feedbackFilter === 'low-quality') {
        return query
            .eq('project_paper_feedback.vote', 'downvote')
            .eq('project_paper_feedback.downvote_reason', 'low_quality');
    }

    return query;
}

function applyFeedFilters(
    query: any,
    projectId: string,
    filter: ProjectFeedRelevanceFilter,
    feedbackFilter: ProjectFeedFeedbackFilter
) {
    query = query.eq('project_id', projectId);

    if (filter === 'must-read') {
        query = query.eq('relevance_class', 2);
    } else if (filter === 'related') {
        query = query.eq('relevance_class', 1);
    } else {
        query = query.in('relevance_class', [1, 2]);
    }

    return applyFeedbackFilter(query, feedbackFilter);
}

function applyFeedSort(query: any, sort: ProjectFeedSortOption) {
    if (sort === 'relevance') {
        return query
            .order('relevance_class', { ascending: false, nullsFirst: false })
            .order('relevance_score', { ascending: false, nullsFirst: false })
            .order('ready_at', { ascending: false });
    }

    return query
        .order('ready_at', { ascending: false })
        .order('relevance_class', { ascending: false, nullsFirst: false })
        .order('relevance_score', { ascending: false, nullsFirst: false });
}

/**
 * Fetch a limited number of feed papers (for preview on overview page)
 */
export function useProjectFeed(
    projectId: string | undefined,
    filter: ProjectFeedRelevanceFilter = 'all',
    feedbackFilter: ProjectFeedFeedbackFilter = 'all',
    sort: ProjectFeedSortOption = 'date',
    limit?: number,
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: ['project-feed', projectId, filter, feedbackFilter, sort, limit],
        queryFn: async () => {
            if (!projectId) throw new Error('No project ID');

            let query = supabase
                .from('project_paper')
                .select(getFeedSelect(feedbackFilter));

            query = applyFeedFilters(query, projectId, filter, feedbackFilter);
            query = applyFeedSort(query, sort);

            if (limit) {
                query = query.limit(limit);
            }

            const { data, error } = await withTimeout(
                query,
                FEED_REQUEST_TIMEOUT_MS,
                'Loading feed papers timed out. Please try again.'
            );
            if (error) throw toError(error);
            return (data || []).map(normalizeFeedPaper);
        },
        enabled: !!projectId && (options.enabled ?? true),
        retry: 1,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
    });
}

/**
 * Infinite-scroll feed for the full feed page
 */
export function useProjectFeedInfinite(
    projectId: string | undefined,
    filter: ProjectFeedRelevanceFilter = 'all',
    feedbackFilter: ProjectFeedFeedbackFilter = 'all',
    sort: ProjectFeedSortOption = 'date',
    options: { enabled?: boolean } = {}
) {
    return useInfiniteQuery({
        queryKey: ['project-feed-infinite', projectId, filter, feedbackFilter, sort],
        queryFn: async ({ pageParam = 0, signal }: { pageParam: number; signal?: AbortSignal }) => {
            if (!projectId) throw new Error('No project ID');

            const from = pageParam * PAGE_SIZE;
            const to = from + PAGE_SIZE - 1;

            let query = supabase
                .from('project_paper')
                .select(getFeedSelect(feedbackFilter));

            query = applyFeedFilters(query, projectId, filter, feedbackFilter);
            query = applyFeedSort(query, sort);
            query = query.range(from, to);
            if (signal) {
                query = query.abortSignal(signal);
            }

            const { data, error } = await withTimeout(
                query,
                FEED_REQUEST_TIMEOUT_MS,
                'Loading feed papers timed out. Please try again.'
            );
            if (error) throw toError(error);
            return {
                papers: (data || []).map(normalizeFeedPaper),
                page: pageParam,
            };
        },
        getNextPageParam: (lastPage) =>
            lastPage.papers.length === PAGE_SIZE ? lastPage.page + 1 : undefined,
        initialPageParam: 0,
        enabled: !!projectId && (options.enabled ?? true),
        retry: 1,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
    });
}

function getFeedCountSelect(feedbackFilter: ProjectFeedFeedbackFilter) {
    const feedbackRelation = feedbackFilter === 'all' || feedbackFilter === 'unrated'
        ? 'project_paper_feedback'
        : 'project_paper_feedback!inner';
    return `id, ${feedbackRelation}(vote, downvote_reason)`;
}

export function useProjectFeedCount(
    projectId: string | undefined,
    filter: ProjectFeedRelevanceFilter = 'all',
    feedbackFilter: ProjectFeedFeedbackFilter = 'all',
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: ['project-feed-count', projectId, filter, feedbackFilter],
        queryFn: async ({ signal }) => {
            if (!projectId) return 0;

            let query = supabase
                .from('project_paper')
                .select(getFeedCountSelect(feedbackFilter), { count: 'exact', head: true });

            query = applyFeedFilters(query, projectId, filter, feedbackFilter);
            if (signal) {
                query = query.abortSignal(signal);
            }

            const { count, error } = await withTimeout(
                query,
                FEED_REQUEST_TIMEOUT_MS,
                'Loading feed count timed out. Please try again.'
            );

            if (error) throw toError(error);
            return count || 0;
        },
        enabled: !!projectId && (options.enabled ?? true),
        retry: 1,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
    });
}
